import { DEFAULT_CATEGORY_ID } from "@bitwarden/web-vault/app/models/constants/category.constants";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";

/**
 * From a list sorted newest-first by `dc`, pick the latest transaction whose srId matches an
 * existing source transaction id. Falls back to the absolute latest.
 */
export function pickBestTransactionDuplicateKeeper(
  sortedNewestFirst: StoreData[],
  sourceIdSet: Set<string>,
): StoreData {
  for (const tx of sortedNewestFirst) {
    const srId = String((tx as TransactionStoreModel).srId ?? "").trim();
    if (srId && sourceIdSet.has(srId)) {
      return tx;
    }
  }
  return sortedNewestFirst[0];
}

/**
 * Style-ID grouping only: sorted newest-first by `dc`. Prefer rows whose srId exists on a source
 * transaction and that have at least one allocation whose category is not the default "Uncategorized"
 * id; among those, the first entry wins (newest). Falls back like {@link pickBestTransactionDuplicateKeeper},
 * then uncategorized-but-linked, then categorized-but-unlinked, then absolute newest.
 */
export function pickBestTransactionDuplicateKeeperStyleId(
  sortedNewestFirst: StoreData[],
  sourceIdSet: Set<string>,
): StoreData {
  const validSrId = (tx: StoreData): boolean => {
    const srId = String((tx as TransactionStoreModel).srId ?? "").trim();
    return srId !== "" && sourceIdSet.has(srId);
  };

  const hasNonUncategorizedCategory = (tx: StoreData): boolean => {
    const alc = (tx as TransactionStoreModel).alc ?? [];
    return alc.some((a) => {
      const ctId = String(a.ctId ?? "").trim();
      return ctId !== "" && ctId !== DEFAULT_CATEGORY_ID;
    });
  };

  for (const tx of sortedNewestFirst) {
    if (validSrId(tx) && hasNonUncategorizedCategory(tx)) {
      return tx;
    }
  }
  for (const tx of sortedNewestFirst) {
    if (validSrId(tx)) {
      return tx;
    }
  }
  for (const tx of sortedNewestFirst) {
    if (hasNonUncategorizedCategory(tx)) {
      return tx;
    }
  }
  return sortedNewestFirst[0];
}
